import { computed, reactive, ref } from "vue";
import {
  ANSWER_TYPES,
  riskAssessmentsApi,
  SKIPPED_VERSION_TYPE,
  summarizeAnswers,
  type EditableAssessment,
  type PreviousAssessment,
  type RiskAssessmentAnswer,
  type RiskAssessmentAnswerInput,
  type RiskAssessmentAnswerType,
  type RiskAssessmentResult,
  type RiskAssessmentScoreSummary,
  type RiskAssessmentVersion
} from "@/vue/entities/risk-assessment";
import { resolveErrorMessage, extractErrorCode } from "./errorMessages";
import type { AnswerDraft, RiskAssessmentFormMode, RiskAssessmentRecoveryCode } from "./types";
import type { RiskAssessmentDraft } from "./useRiskAssessmentDraft";

// 入力ダイアログの状態と操作をまとめて公開する。
// ref ではなく reactive で返すため、テンプレートから `form.summary` のように .value なしで参照できる。
export type RiskAssessmentForm = {
  versions: RiskAssessmentVersion[];
  version: RiskAssessmentVersion | null;
  selectedVersionType: number | null;
  // 「どちらにも該当しない」を選択中か。設問を描画せず完了だけできる。
  isSkipped: boolean;
  skipVersionLabel: string;
  mode: RiskAssessmentFormMode;
  editableAssessment: EditableAssessment | null;
  drafts: AnswerDraft[];
  draftMap: Record<number, AnswerDraft>;
  previousAssessment: PreviousAssessment | null;
  previousAnswerMap: Record<number, RiskAssessmentAnswer>;
  summary: RiskAssessmentScoreSummary;
  completable: boolean;
  isSubmitting: boolean;
  errorMessage: string;
  // 入力内容の再取得・モード切替が必要なエラーを widget へ伝える。
  recoveryCode: RiskAssessmentRecoveryCode | null;
  selectVersion: (versionType: number) => void;
  selectAnswerType: (questionNo: number, answerType: RiskAssessmentAnswerType) => void;
  toggleDetail: (questionNo: number, detailKey: string) => void;
  submit: () => Promise<RiskAssessmentResult | null>;
  clearError: () => void;
  clearRecovery: () => void;
};

const EMPTY_SUMMARY: RiskAssessmentScoreSummary = {
  applicableCount: 0,
  notApplicableCount: 0,
  unknownCount: 0,
  unansweredCount: 0,
  answeredAll: false,
  positive: false
};

const RECOVERABLE_ERROR_CODES: RiskAssessmentRecoveryCode[] = ["stale_object", "edit_period_expired"];

const toRecoveryCode = (error: unknown): RiskAssessmentRecoveryCode | null => {
  const code = extractErrorCode(error);

  return RECOVERABLE_ERROR_CODES.find((candidate) => candidate === code) ?? null;
};

export const useRiskAssessmentForm = (draft: RiskAssessmentDraft): RiskAssessmentForm => {
  const isSubmitting = ref(false);
  const errorMessage = ref("");
  const recoveryCode = ref<RiskAssessmentRecoveryCode | null>(null);

  const versions = computed<RiskAssessmentVersion[]>(() => draft.newResponse.value?.versions ?? []);

  const isSkipped = computed<boolean>(() => draft.selectedVersionType.value === SKIPPED_VERSION_TYPE);

  const skipVersionLabel = computed<string>(() => draft.newResponse.value?.skipVersionLabel ?? "");

  const version = computed<RiskAssessmentVersion | null>(
    () => versions.value.find((candidate) => candidate.versionType === draft.selectedVersionType.value) ?? null
  );

  const drafts = computed<AnswerDraft[]>(() => {
    const versionType = draft.selectedVersionType.value;
    if (versionType === null) return [];

    return draft.draftByVersion.value[versionType] ?? [];
  });

  const draftMap = computed<Record<number, AnswerDraft>>(() =>
    Object.fromEntries(drafts.value.map((answer) => [answer.questionNo, answer]))
  );

  const previousAssessment = computed<PreviousAssessment | null>(() => {
    const versionType = draft.selectedVersionType.value;
    if (versionType === null) return null;

    return draft.newResponse.value?.previousAssessments?.[String(versionType)] ?? null;
  });

  const previousAnswerMap = computed<Record<number, RiskAssessmentAnswer>>(() =>
    Object.fromEntries((previousAssessment.value?.answers ?? []).map((answer) => [answer.questionNo, answer]))
  );

  const summary = computed<RiskAssessmentScoreSummary>(() => {
    if (!version.value) return EMPTY_SUMMARY;

    return summarizeAnswers(drafts.value, {
      questionCount: version.value.questionCount,
      positiveThreshold: version.value.positiveThreshold
    });
  });

  // 全設問に回答するまで完了できない（FR-007 / REQ-007 / AC-002）。
  // 未回答は該当に数えられず陽性になりにくくなるため、サーバー側でも incomplete_answers で弾く。
  // スキップは設問に答えないため、選択した時点で完了できる。
  const completable = computed<boolean>(() => {
    if (isSubmitting.value) return false;
    if (isSkipped.value) return true;

    return version.value !== null && summary.value.answeredAll;
  });

  const draftOf = (questionNo: number): AnswerDraft | null => draftMap.value[questionNo] ?? null;

  const selectVersion = (versionType: number): void => {
    draft.selectedVersionType.value = versionType;
  };

  // 「該当 / 非該当 / 不明」は answerType という単一の値で表現するため、排他性がデータ構造で保証される（FR-006 / REQ-005）。
  // 選択済みの選択肢をもう一度押した場合は未回答へ戻す。multiSelect 設問はサブ項目を全て外すと
  // 未回答へ戻るため、単一選択の設問だけ取り消せないと操作が非対称になるため揃える。
  // 未回答へ戻した設問が残っている間は completable が false になり、完了はできない（FR-007）。
  const selectAnswerType = (questionNo: number, answerType: RiskAssessmentAnswerType): void => {
    const target = draftOf(questionNo);
    if (!target) return;

    const nextAnswerType = target.answerType === answerType ? null : answerType;

    target.answerType = nextAnswerType;
    // 「該当」以外になった時点でサブ項目の選択は破棄する（サーバー側検証と整合）。
    if (nextAnswerType !== ANSWER_TYPES.applicable) target.detailKeys = [];
  };

  // サブ項目をチェックしたら自動的に「該当」になり、すべて外すと未回答に戻る（UI上は「該当＝サブ項目1件以上」）。
  const toggleDetail = (questionNo: number, detailKey: string): void => {
    const target = draftOf(questionNo);
    if (!target) return;

    target.detailKeys = target.detailKeys.includes(detailKey)
      ? target.detailKeys.filter((key) => key !== detailKey)
      : [...target.detailKeys, detailKey];
    target.answerType = target.detailKeys.length > 0 ? ANSWER_TYPES.applicable : null;
  };

  // 全23問を必ず送る。completable の判定により未選択（0）のまま送信されることはないが、
  // 間引いて送るとサーバー側で「設問が欠けている」のか「未回答なのか」を区別できないため形は固定する。
  const buildAnswerInputs = (): RiskAssessmentAnswerInput[] =>
    drafts.value.map((answer) => ({
      question_no: answer.questionNo,
      answer_type: answer.answerType ?? ANSWER_TYPES.unanswered,
      detail_keys: [...answer.detailKeys]
    }));

  const clearError = (): void => {
    errorMessage.value = "";
  };

  const clearRecovery = (): void => {
    recoveryCode.value = null;
  };

  const submit = async (): Promise<RiskAssessmentResult | null> => {
    const response = draft.newResponse.value;
    const selected = version.value;
    // スキップ時は入力シートを選んでいないため selected は null のまま送信する。
    if (!response || isSubmitting.value) return null;
    if (!selected && !isSkipped.value) return null;

    isSubmitting.value = true;
    errorMessage.value = "";
    recoveryCode.value = null;
    try {
      const payload = {
        consultable_type: response.consultable.type,
        consultable_id: response.consultable.id,
        version_type: isSkipped.value ? null : selected!.versionType,
        answers: isSkipped.value ? [] : buildAnswerInputs()
      };
      const editable = draft.editableAssessment.value;

      return draft.mode.value === "edit" && editable
        ? await riskAssessmentsApi.update(editable.id, { ...payload, lock_version: editable.lockVersion })
        : await riskAssessmentsApi.create(payload);
    } catch (error) {
      // 送信に失敗しても入力内容は保持したままダイアログを開き続ける（NR-001 / E-22）。
      errorMessage.value = resolveErrorMessage(error);
      // 同じ lock_version / 修正モードのまま再送信しても必ず失敗するため、回復が必要な旨を上位へ通知する。
      recoveryCode.value = toRecoveryCode(error);
      return null;
    } finally {
      isSubmitting.value = false;
    }
  };

  return reactive({
    versions,
    version,
    selectedVersionType: draft.selectedVersionType,
    isSkipped,
    skipVersionLabel,
    mode: draft.mode,
    editableAssessment: draft.editableAssessment,
    drafts,
    draftMap,
    previousAssessment,
    previousAnswerMap,
    summary,
    completable,
    isSubmitting,
    errorMessage,
    recoveryCode,
    selectVersion,
    selectAnswerType,
    toggleDetail,
    submit,
    clearError,
    clearRecovery
  });
};
